/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { t } from 'i18next';
import { Card, Link, styled, Typography, Grid, Box } from '@mui/material';
import { Data, Domain, Hooks } from '@3nickels/data-modules';
import { useService } from '@aesop-fables/containr-react';
import { PaymentApi } from '../../../api/apis/PaymentApi';
import { ApiKeys } from '../../../api/apis/ApiKeys';
import { Colors } from '../../../themes';
import UnsubscribeModal from './UnsubscribeModal';
import ResubscribeModal from './ResubscribeModal';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { Spacer, CornerRibbon, MobileCornerRibbon } from '../../../components';
import { getDescription, getIntervalText, getPlanInfo, getSubtextsAndActionButton } from './utils';
import PricebookOptionLearnMoreDrawer from '../../../sign-up/welcome/components/PricebookOptionLearnMoreDrawer';
import { useMessage } from '../../../hooks/useMessage';
import { useLoading } from '../../../hooks/useLoading';

const Banner = styled('div')({
  position: 'absolute',
  left: 0,
  top: 0,
  padding: '10px 30px',
  width: '100%',
  backgroundColor: Colors.primaryLightest,
  zIndex: 1,
});

const MobileBanner = styled('div')({
  left: 0,
  top: 0,
  padding: '10px 30px',
  width: '100%',
  backgroundColor: Colors.primaryLightest,
  borderRadius: '10px 10px 0 0',
  zIndex: 1,
});

export type PlanCardPages = 'ManageSubscription' | 'ChangePlan';

export interface PlanCardProps {
  plan: Domain.PricebookBundle;
  pricebookMetadata: any;
  subscription: Domain.SubscriptionInfo | undefined;
  threeNickelsProducts: Domain.PricebookBundle[] | undefined;
  inAdviceFreeTrial: boolean;
  pageContext: PlanCardPages;
  freeTrialStatus?: Data.Users.FreeTrialInfoDto | undefined;
  footnoteIndex?: number;
  currentPlan?: Domain.PricebookBundle | undefined;
  orgDoesNotOfferFreePricebook?: boolean | undefined;
  userNeverSubscribedToExclusivePricebook?: boolean;
  onlyOffersFreeAndOnePaidPricebook?: boolean;
  mobileCardStyle?: React.CSSProperties;
  userIsUnderFreemiumModel?: boolean;
  isFreemium?: boolean;
  isPremium?: boolean;
  hideButton?: boolean;
  hideDetails?: boolean;
  hidePrice?: boolean;
  learnMore?: boolean;
  onLearnMore?: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  pricebookMetadata,
  subscription,
  threeNickelsProducts,
  inAdviceFreeTrial,
  freeTrialStatus,
  footnoteIndex,
  pageContext,
  currentPlan,
  orgDoesNotOfferFreePricebook,
  userNeverSubscribedToExclusivePricebook,
  onlyOffersFreeAndOnePaidPricebook,
  mobileCardStyle,
  userIsUnderFreemiumModel,
  isFreemium,
  isPremium,
  hideButton,
  hideDetails,
  onLearnMore,
  hidePrice,
}) => {
  const planInfo = getPlanInfo(plan, subscription, freeTrialStatus);
  const {
    orgPays,
    isAddOn,
    nickelsU,
    promotion,
    isFree,
    basePrice,
    promoPrice,
    artUrl,
    pricebookName,
  } = planInfo;

  const commands = Hooks.useCommands();
  const paymentApi = useService<PaymentApi>(ApiKeys.Payment);
  const { showMessage } = useMessage();
  const { setLoading } = useLoading();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);
  const [resubscribeModalOpen, setResubscribeModalOpen] = useState(false);
  const { isMobile, isTablet } = useDeviceType();
  const mobile = isMobile || isTablet;
  const subscriptionStatus = subscription?.mappedStatus;

  const isCurrentPlan = plan.pricebook.id === currentPlan?.pricebook.id;
  const isIncludedPlan =
    isAddOn &&
    subscription?.pricebookId === plan.pricebook.id &&
    (subscriptionStatus?.active || (subscriptionStatus?.cancelled && !subscriptionStatus?.expired));
  const hasBanner = isCurrentPlan || isIncludedPlan || (userIsUnderFreemiumModel && isFreemium);
  const learnMore = isAddOn && !isIncludedPlan;

  const usePriceAfterPromo = pageContext === 'ManageSubscription' && nickelsU && promotion;
  const index = usePriceAfterPromo ? 1 : 0;
  const price = usePriceAfterPromo ? (promoPrice ?? basePrice) : basePrice;
  const intervalText = getIntervalText(plan.prices[index].intervalType);

  const metadata = useMemo(() => {
    if (!pricebookMetadata || Object.keys(pricebookMetadata).length === 0) return;
    return pricebookMetadata?.find((m: any) => m.id === plan.pricebook.id);
  }, [plan, pricebookMetadata]);

  const description = getDescription(plan);

  const titleText = isAddOn
    ? t('PremiumAddOn')
    : isFreemium
      ? '3Nickels'
      : isFree
        ? 'Basic Tools'
        : pricebookName;
  const descriptionText = isAddOn
    ? isIncludedPlan
      ? t('YoureCurrentlySubscribed')
      : t('UpgradeToUnlock')
    : description;

  const { paymentSubtext, paymentSubtextColor, titleSubtext, titleSubtextColor, actionButton } =
    getSubtextsAndActionButton(
      planInfo,
      plan,
      subscription,
      isCurrentPlan,
      pageContext,
      mobile,
      paymentApi,
      commands,
      setUnsubscribeModalOpen,
      userIsUnderFreemiumModel,
      orgDoesNotOfferFreePricebook,
      inAdviceFreeTrial,
      userNeverSubscribedToExclusivePricebook,
      onlyOffersFreeAndOnePaidPricebook,
      setLoading,
      showMessage,
      setResubscribeModalOpen
    );

  return (
    <>
      <PricebookOptionLearnMoreDrawer
        pricebookBundle={plan}
        metadata={metadata}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        freemiumPricebook={planInfo.freemium}
      />
      {subscription && (
        <>
          <UnsubscribeModal
            modalOpen={unsubscribeModalOpen}
            setModalOpen={setUnsubscribeModalOpen}
            subscription={subscription}
            threeNickelsProducts={threeNickelsProducts}
          />
          <ResubscribeModal
            modalOpen={resubscribeModalOpen}
            setModalOpen={setResubscribeModalOpen}
            subscription={subscription}
            threeNickelsProducts={threeNickelsProducts}
          />
        </>
      )}

      {mobile ? (
        <Grid
          sx={{
            position: 'relative',
            borderRadius: '10px',
            border: `solid 1px ${Colors.primaryLightest}`,
            backgroundColor: Colors.tertiaryBase,
            width: '100%',
            overflow: 'hidden',
            ...mobileCardStyle,
          }}>
          {isPremium && <MobileCornerRibbon />}

          {hasBanner && (
            <MobileBanner>
              <Typography
                variant='p14Bold'
                color='secondary'
                style={{
                  justifyContent: 'center',
                }}>
                {isIncludedPlan ? t('Included') : t('CurrentPlan')}
              </Typography>
            </MobileBanner>
          )}

          <Grid
            sx={{
              rowGap: '10px',
              padding: isCurrentPlan ? '20px' : '20px 20px 30px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img src={artUrl} alt='Product Art' style={{ height: '60px' }} />

            <Typography variant='p20Bold' color='secondary'>
              {titleText}
            </Typography>

            {titleSubtext && (
              <Typography textAlign='center' variant='p12' color={titleSubtextColor} mx={1}>
                {titleSubtext}
              </Typography>
            )}

            <Typography sx={{ width: '80%', textAlign: 'center' }} variant='p14' color='secondary'>
              {descriptionText}
            </Typography>

            {learnMore ? (
              <Link variant='p16' onClick={onLearnMore ? onLearnMore : () => setDrawerOpen(true)}>
                {t('LearnMore')}
              </Link>
            ) : (
              !hideDetails && (
                <Link variant='p16' onClick={() => setDrawerOpen(true)}>
                  {t('SeeDetails')}
                </Link>
              )
            )}

            <Grid textAlign='center' justifyContent='center'>
              {hidePrice ? null : !orgPays ? (
                isFree ? (
                  <Typography mt='10px' variant='p24Bold' color='primary'>
                    {t('FREE')}
                  </Typography>
                ) : (
                  <Box display='flex' justifyContent='center' alignItems='baseline' mt='20px'>
                    <Typography variant='p24Bold' color='primary'>
                      {price}
                      {footnoteIndex && footnoteIndex > 0 ? '*'.repeat(footnoteIndex) : null}&nbsp;
                    </Typography>

                    {intervalText && (
                      <Typography variant='p16' color='primary' fontWeight='normal'>
                        / {intervalText}
                      </Typography>
                    )}
                  </Box>
                )
              ) : null}

              {paymentSubtext && (
                <Typography
                  textAlign='center'
                  justifyContent='center'
                  variant='p12'
                  color={paymentSubtextColor}
                  mx='15px'
                  mt='5px'>
                  {paymentSubtext}
                </Typography>
              )}
            </Grid>

            {actionButton && !hideButton && actionButton}
          </Grid>
        </Grid>
      ) : (
        <Card className='plan-card'>
          {isPremium && <CornerRibbon />}

          {hasBanner && (
            <Banner>
              <Typography
                variant='p14Bold'
                color='secondary'
                style={{
                  justifyContent: 'center',
                }}>
                {isIncludedPlan ? t('Included') : t('CurrentPlan')}
              </Typography>
            </Banner>
          )}

          <Spacer height='md' />

          <Grid
            container
            style={{
              rowGap: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexGrow: 1,
              height: '95%',
            }}>
            <img src={artUrl} alt='Product Art' style={{ height: '60px' }} />

            <Typography
              variant='p20Bold'
              color='secondary'
              sx={{
                textAlign: 'center',
                maxWidth: '100%',
                minHeight: '28px',
                fontSize: titleText.length > 20 ? '18px' : '20px', // avoid misalignment on long titles
                alignItems: titleText.length > 20 ? 'flex-end' : 'center',
              }}>
              {titleText}
            </Typography>

            {titleSubtext && (
              <Typography
                textAlign={'center'}
                variant='p12'
                color={titleSubtextColor}
                mx={1}
                mb={'5px'}>
                {titleSubtext}
              </Typography>
            )}

            <Typography mx={'20px'} variant='p14' color='secondary' textAlign='center'>
              {descriptionText}
            </Typography>

            {learnMore ? (
              <Link variant='p16' onClick={onLearnMore ? onLearnMore : () => setDrawerOpen(true)}>
                {t('LearnMore')}
              </Link>
            ) : (
              !hideDetails && (
                <Link variant='p16' onClick={() => setDrawerOpen(true)}>
                  {t('SeeDetails')}
                </Link>
              )
            )}

            <Grid textAlign='center' justifyContent='center'>
              {!hidePrice &&
                !orgPays &&
                (isFree ? (
                  <Typography variant='p24Bold' color='primary' justifyContent='center' mt='10px'>
                    {t('FREE')}
                  </Typography>
                ) : (
                  <Box display='flex' justifyContent='center' alignItems='baseline' mt='20px'>
                    <Typography variant='p24Bold' color='primary'>
                      {price}
                      {footnoteIndex && footnoteIndex > 0 ? '*'.repeat(footnoteIndex) : null}
                    </Typography>
                    {intervalText && (
                      <Typography variant='p16' color='primary' fontWeight='normal' ml={0.5}>
                        / {intervalText}
                      </Typography>
                    )}
                  </Box>
                ))}

              {paymentSubtext && (
                <Typography
                  textAlign='center'
                  justifyContent='center'
                  variant='p12'
                  color={paymentSubtextColor}
                  mx='15px'
                  mt='5px'>
                  {paymentSubtext}
                </Typography>
              )}
            </Grid>

            {actionButton && !hideButton && (
              <Box
                sx={{
                  mt: 'auto',
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}>
                {actionButton}
              </Box>
            )}
          </Grid>
        </Card>
      )}
    </>
  );
};

export default PlanCard;
